import { useEffect, useMemo, useState } from "react";
import {
  createFeeChangeRequest,
  getFeeGrid,
  listMyFeeChangeRequests,
  simulateFees,
  type FeeChangeRequest,
  type FeeGrid,
  type SimulateResult,
} from "@/lib/api";
import { formatAmount } from "@/lib/format";
import { PageHeader } from "@/components/PageHeader";
import { CheckCircle2, Clock, Loader2, X } from "lucide-react";

export function PricingPage() {
  const [grid, setGrid] = useState<FeeGrid | null>(null);
  const [loadingGrid, setLoadingGrid] = useState(true);
  const [gridError, setGridError] = useState<string | null>(null);

  const [simVendor, setSimVendor] = useState("orange");
  const [simAmount, setSimAmount] = useState("1000");
  const [simCurrency, setSimCurrency] = useState<"CDF" | "USD">("CDF");
  const [simResult, setSimResult] = useState<SimulateResult | null>(null);
  const [simLoading, setSimLoading] = useState(false);
  const [simError, setSimError] = useState<string | null>(null);

  const [pendingReqs, setPendingReqs] = useState<FeeChangeRequest[]>([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalVendor, setModalVendor] = useState<string>("");
  const [modalAction, setModalAction] = useState<string>("debit");
  const [modalCurrentBearer, setModalCurrentBearer] = useState<"MERCHANT" | "CUSTOMER">("MERCHANT");
  const [modalRequestedBearer, setModalRequestedBearer] = useState<"MERCHANT" | "CUSTOMER">("CUSTOMER");
  const [modalReason, setModalReason] = useState("");
  const [modalSubmitting, setModalSubmitting] = useState(false);
  const [modalError, setModalError] = useState<string | null>(null);
  const [modalSuccess, setModalSuccess] = useState<FeeChangeRequest | null>(null);

  const refreshRequests = async () => {
    try {
      const r = await listMyFeeChangeRequests("pending");
      setPendingReqs(r.requests);
    } catch {}
  };

  useEffect(() => {
    (async () => {
      try {
        const g = await getFeeGrid();
        setGrid(g);
        if (g.grid.length > 0) setSimVendor(g.grid[0].vendor);
      } catch (e) {
        setGridError(e instanceof Error ? e.message : "Erreur de chargement");
      } finally {
        setLoadingGrid(false);
      }
    })();
    refreshRequests();
  }, []);

  const pendingByKey = useMemo(() => {
    const m: Record<string, FeeChangeRequest> = {};
    for (const p of pendingReqs) {
      m[`${p.vendor}|${p.action}`] = p;
    }
    return m;
  }, [pendingReqs]);

  const openRequestModal = (vendor: string, action: string, currentBearer: "MERCHANT" | "CUSTOMER") => {
    setModalVendor(vendor);
    setModalAction(action);
    setModalCurrentBearer(currentBearer);
    setModalRequestedBearer(currentBearer === "MERCHANT" ? "CUSTOMER" : "MERCHANT");
    setModalReason("");
    setModalError(null);
    setModalSuccess(null);
    setModalOpen(true);
  };

  const submitRequest = async (e: React.FormEvent) => {
    e.preventDefault();
    if (modalReason.trim().length < 20) {
      setModalError("Merci de donner au moins 20 caractères de justification.");
      return;
    }
    setModalSubmitting(true);
    setModalError(null);
    try {
      const created = await createFeeChangeRequest({
        vendor: modalVendor,
        action: modalAction as "debit" | "credit",
        requested_bearer: modalRequestedBearer,
        reason: modalReason.trim(),
      });
      setModalSuccess(created);
      await refreshRequests();
    } catch (err) {
      setModalError(err instanceof Error ? err.message : "Erreur de soumission");
    } finally {
      setModalSubmitting(false);
    }
  };

  const runSimulator = async () => {
    setSimLoading(true);
    setSimError(null);
    try {
      const amt = Number(simAmount);
      if (!Number.isFinite(amt) || amt <= 0) throw new Error("Montant invalide");
      const r = await simulateFees({ amount: amt, currency: simCurrency, vendor: simVendor });
      setSimResult(r);
    } catch (e) {
      setSimError(e instanceof Error ? e.message : "Erreur de simulation");
      setSimResult(null);
    } finally {
      setSimLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="Frais & Tarification"
        subtitle="Grille contractuelle par méthode et simulateur de découpage. Lecture seule — pour changer un tarif, contactez le support."
      />

      {/* Grille */}
      <section className="bg-white rounded-lg border border-gray-200 shadow-sm">
        <header className="px-6 py-4 border-b border-gray-100">
          <h2 className="text-base font-semibold text-[#181c32]">Grille par méthode</h2>
          <p className="text-sm text-[#7e8299]">
            Une ligne par opérateur, avec votre taux contractuel et qui absorbe les frais.
          </p>
        </header>
        {loadingGrid ? (
          <div className="p-6 text-center text-[#7e8299]"><Loader2 className="inline animate-spin" /> Chargement…</div>
        ) : gridError ? (
          <div className="p-6 text-danger-700">Erreur : {gridError}</div>
        ) : !grid || grid.grid.length === 0 ? (
          <div className="p-6 text-center text-[#7e8299]">Aucun tarif configuré pour votre compte.</div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 text-left text-xs uppercase tracking-wide text-[#7e8299]">
                <th className="px-6 py-3">Méthode</th>
                <th className="px-6 py-3 text-right">Votre taux</th>
                <th className="px-6 py-3">Qui paie</th>
                <th className="px-6 py-3">Rail</th>
                <th className="px-6 py-3 text-right"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {grid.grid.map((r) => {
                const pending = pendingByKey[`${r.vendor}|${r.action}`];
                return (
                  <tr key={r.vendor + r.action} className="hover:bg-gray-50">
                    <td className="px-6 py-3 font-semibold text-[#181c32] capitalize">{r.vendor}</td>
                    <td className="px-6 py-3 text-right font-medium text-[#181c32]">{r.merchant_rate_pct.toFixed(2)} %</td>
                    <td className="px-6 py-3">
                      {r.fee_bearer === "CUSTOMER" ? (
                        <span className="inline-flex items-center rounded px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide bg-sky-100 text-sky-800 border border-sky-200">
                          Client
                        </span>
                      ) : (
                        <span className="inline-flex items-center rounded px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide bg-slate-100 text-slate-700 border border-slate-200">
                          Vous
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-3 text-xs text-[#7e8299]">{r.is_ecommerce_rail ? "Ecommerce" : "Standard"}</td>
                    <td className="px-6 py-3 text-right">
                      {pending ? (
                        <span
                          className="inline-flex items-center gap-1 rounded px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide bg-amber-100 text-amber-800 border border-amber-200"
                          title={`Demande soumise le ${new Date(pending.requested_at).toLocaleDateString()} — passe à ${pending.requested_bearer === "CUSTOMER" ? "Client" : "Vous"}`}
                        >
                          <Clock size={11} /> En cours
                        </span>
                      ) : (
                        <button
                          onClick={() => openRequestModal(r.vendor, r.action, r.fee_bearer)}
                          className="text-xs font-medium text-sky-700 hover:underline"
                        >
                          Demander
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
        <footer className="px-6 py-3 border-t border-gray-100 bg-gray-50 flex justify-between items-center">
          <p className="text-xs text-[#7e8299]">Compte : <span className="font-mono">{grid?.merchant_code ?? "—"}</span></p>
          <p className="text-xs text-[#7e8299]">
            Cliquez sur <b>Demander</b> à droite d'une ligne pour soumettre un changement. Chaque demande est revue par notre équipe.
          </p>
        </footer>
      </section>

      {/* Simulateur */}
      <section className="bg-white rounded-lg border border-gray-200 shadow-sm">
        <header className="px-6 py-4 border-b border-gray-100">
          <h2 className="text-base font-semibold text-[#181c32]">Simulateur de découpage</h2>
          <p className="text-sm text-[#7e8299]">
            Pour un montant hypothétique, calcule exactement ce que le client paie, ce que vous touchez, et la part Moko/telco.
            Utilise la même formule que le callback réel.
          </p>
        </header>
        <div className="p-6 grid gap-6 lg:grid-cols-2">
          <div className="space-y-4">
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wide text-[#7e8299] mb-1">Méthode</label>
              <select
                className="w-full rounded border border-gray-300 px-3 py-2 text-sm capitalize"
                value={simVendor}
                onChange={(e) => setSimVendor(e.target.value)}
              >
                {grid?.grid.map((r) => (
                  <option key={r.vendor} value={r.vendor} className="capitalize">{r.vendor}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wide text-[#7e8299] mb-1">Montant marchand</label>
              <div className="flex gap-2">
                <input
                  type="number"
                  min="1"
                  step="1"
                  value={simAmount}
                  onChange={(e) => setSimAmount(e.target.value)}
                  className="flex-1 rounded border border-gray-300 px-3 py-2 text-sm font-mono"
                />
                <select
                  className="rounded border border-gray-300 px-3 py-2 text-sm"
                  value={simCurrency}
                  onChange={(e) => setSimCurrency(e.target.value as "CDF" | "USD")}
                >
                  <option value="CDF">CDF</option>
                  <option value="USD">USD</option>
                </select>
              </div>
              <p className="mt-1 text-xs text-[#7e8299]">Le montant que vous souhaitez recevoir sur votre wallet.</p>
            </div>
            <button
              onClick={runSimulator}
              disabled={simLoading}
              className="w-full rounded bg-[#181c32] hover:bg-[#0f1220] disabled:opacity-50 text-white text-sm font-semibold py-2.5"
            >
              {simLoading ? (
                <>
                  <Loader2 className="inline animate-spin" /> Calcul…
                </>
              ) : (
                "Simuler"
              )}
            </button>
            {simError && <div className="text-sm text-danger-700">{simError}</div>}
          </div>

          <div>
            {simResult ? (
              <SimulatorResult r={simResult} />
            ) : (
              <div className="h-full flex items-center justify-center text-sm text-[#7e8299] text-center border-2 border-dashed border-gray-200 rounded p-8">
                Choisissez une méthode et un montant, puis cliquez sur Simuler.
              </div>
            )}
          </div>
        </div>
      </section>

      {modalOpen && (
        <RequestModal
          vendor={modalVendor}
          action={modalAction}
          currentBearer={modalCurrentBearer}
          requestedBearer={modalRequestedBearer}
          onRequestedBearerChange={setModalRequestedBearer}
          reason={modalReason}
          onReasonChange={setModalReason}
          submitting={modalSubmitting}
          error={modalError}
          success={modalSuccess}
          onClose={() => setModalOpen(false)}
          onSubmit={submitRequest}
        />
      )}
    </div>
  );
}

function RequestModal({
  vendor, action, currentBearer, requestedBearer, onRequestedBearerChange,
  reason, onReasonChange, submitting, error, success, onClose, onSubmit,
}: {
  vendor: string; action: string;
  currentBearer: "MERCHANT" | "CUSTOMER";
  requestedBearer: "MERCHANT" | "CUSTOMER";
  onRequestedBearerChange: (b: "MERCHANT" | "CUSTOMER") => void;
  reason: string; onReasonChange: (r: string) => void;
  submitting: boolean; error: string | null; success: FeeChangeRequest | null;
  onClose: () => void;
  onSubmit: (e: React.FormEvent) => void;
}) {
  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center p-4">
      <div className="fixed inset-0 bg-black/40" onClick={onClose} />
      <div className="relative bg-white rounded-lg shadow-xl w-full max-w-lg">
        <header className="px-5 py-3 border-b border-gray-100 flex items-center justify-between">
          <div>
            <div className="text-base font-semibold text-[#181c32]">Changer le porteur des frais</div>
            <div className="text-xs text-[#7e8299]"><span className="capitalize">{vendor}</span> · {action}</div>
          </div>
          <button onClick={onClose} className="p-1.5 rounded hover:bg-gray-100"><X size={16} /></button>
        </header>
        {success ? (
          <div className="p-6 space-y-3">
            <div className="flex items-center gap-2 text-emerald-800">
              <CheckCircle2 size={22} className="text-emerald-600" />
              <div className="font-semibold">Demande soumise</div>
            </div>
            <div className="text-sm text-[#3f4254]">
              Elle sera examinée par notre équipe. Vous recevrez la décision par email et l'état affiché ici passera automatiquement à "Approuvé" ou "Rejeté".
            </div>
            <div className="rounded border border-gray-200 bg-gray-50 p-3 text-xs text-[#3f4254] space-y-0.5">
              <div>Référence : <span className="font-mono">#{success.id}</span></div>
              <div>Demandé : {new Date(success.requested_at).toLocaleString()}</div>
              <div>Passe de <b>{success.current_bearer === "CUSTOMER" ? "Client" : "Vous"}</b> à <b>{success.requested_bearer === "CUSTOMER" ? "Client" : "Vous"}</b></div>
            </div>
            <div className="flex justify-end">
              <button onClick={onClose} className="rounded bg-[#181c32] hover:bg-[#0f1220] px-4 py-2 text-sm font-semibold text-white">Fermer</button>
            </div>
          </div>
        ) : (
          <form onSubmit={onSubmit} className="p-6 space-y-4">
            <div className="rounded border border-gray-200 bg-gray-50 p-3 text-xs text-[#3f4254] space-y-0.5">
              <div>Actuel : <b>{currentBearer === "CUSTOMER" ? "Le client paie les frais" : "Vous absorbez les frais"}</b></div>
              <div>Souhaité : <b>{requestedBearer === "CUSTOMER" ? "Le client paie les frais" : "Vous absorbez les frais"}</b></div>
            </div>
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wide text-[#7e8299] mb-1">Qui paie désormais ?</label>
              <div className="grid grid-cols-2 gap-2">
                {(["MERCHANT", "CUSTOMER"] as const).map((b) => (
                  <button
                    key={b}
                    type="button"
                    onClick={() => onRequestedBearerChange(b)}
                    disabled={b === currentBearer}
                    className={`rounded border-2 p-3 text-left text-sm ${
                      b === currentBearer ? "border-gray-200 bg-gray-50 opacity-50 cursor-not-allowed" :
                      requestedBearer === b ? "border-[#181c32] bg-white" : "border-gray-200 hover:border-gray-300"
                    }`}
                  >
                    <div className="font-semibold">{b === "CUSTOMER" ? "Le client" : "Moi (marchand)"}</div>
                    <div className="text-xs text-[#7e8299]">{b === "CUSTOMER" ? "STK gonflé, marchand encaisse net" : "Frais déduits du montant reçu"}</div>
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wide text-[#7e8299] mb-1">
                Motif (min 20 caractères) · {reason.length}/500
              </label>
              <textarea
                value={reason}
                onChange={(e) => onReasonChange(e.target.value.slice(0, 500))}
                rows={4}
                required
                minLength={20}
                placeholder="Ex : nos clients sont prêts à payer les frais, notre marge est trop mince sur Orange…"
                className="w-full rounded border border-gray-300 px-3 py-2 text-sm resize-none focus:outline-none focus:border-[#181c32]"
              />
            </div>
            {error && <div className="text-sm text-danger-700">{error}</div>}
            <div className="flex justify-end gap-2">
              <button type="button" onClick={onClose} className="rounded border border-gray-300 px-4 py-2 text-sm text-[#3f4254] hover:bg-gray-50">
                Annuler
              </button>
              <button
                type="submit"
                disabled={reason.trim().length < 20 || submitting || requestedBearer === currentBearer}
                className="rounded bg-[#181c32] hover:bg-[#0f1220] disabled:opacity-50 px-4 py-2 text-sm font-semibold text-white flex items-center gap-2"
              >
                {submitting && <Loader2 size={14} className="animate-spin" />}
                {submitting ? "Envoi…" : "Soumettre la demande"}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

function SimulatorResult({ r }: { r: SimulateResult }) {
  const cur = r.input.currency;
  const fmt = (v: number) => formatAmount(Math.round(v * 100), cur);

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <MetricCard
          label="Le client paie"
          value={fmt(r.total_customer_paid)}
          tone="sky"
          hint={r.fee_bearer === "CUSTOMER" ? "STK gonflé pour couvrir les frais" : "Le client règle le montant que vous avez demandé"}
        />
        <MetricCard
          label="Vous encaissez"
          value={fmt(r.merchant_net)}
          tone="emerald"
          hint="Crédité sur votre wallet après commission"
        />
      </div>
      <div className="rounded border border-gray-200 divide-y divide-gray-100">
        <BreakdownRow label="Marge Moko" value={fmt(r.moko_fee)} sub={`${((r.moko_fee / r.total_customer_paid) * 100).toFixed(2)} % du total`} />
        <BreakdownRow
          label="Part telco (wallet Moko)"
          value={fmt(r.telco_fee_moko_wallet)}
          sub={r.telco_paid_by === "MOKO" ? `${r.telco_share_effective_pct.toFixed(2)} % versé au telco depuis notre wallet préfinancé` : "0 sur ce rail — le telco facture directement le client"}
        />
        {r.telco_fee_customer_paid > 0 && (
          <BreakdownRow
            label="Part telco (facturée au client, hors STK)"
            value={fmt(r.telco_fee_customer_paid)}
            sub="Airtel prélève cette part directement au client — ne figure pas sur le STK"
          />
        )}
      </div>
      <div className="text-xs text-[#7e8299] space-y-1 border-t border-gray-100 pt-3">
        <div>Votre taux contractuel : <span className="font-mono font-semibold">{r.merchant_rate_pct.toFixed(2)} %</span></div>
        <div>Frais supportés par : <span className="font-semibold">{r.fee_bearer === "CUSTOMER" ? "le client" : "vous (marchand)"}</span></div>
        <div>Rail telco : <span className="font-semibold">{r.telco_paid_by === "CUSTOMER" ? "facturation directe client" : "prélèvement wallet Moko"}</span></div>
        {r.stk_floored && <div className="text-amber-700 font-semibold">⚠ Plancher de rentabilité déclenché — grille tarifaire trop mince.</div>}
        {r.moko_margin_negative && <div className="text-danger-700 font-semibold">⚠ Marge Moko négative — Moko vend à perte sur cette transaction.</div>}
      </div>
    </div>
  );
}

function MetricCard({ label, value, tone, hint }: { label: string; value: string; tone: "sky" | "emerald"; hint?: string }) {
  const bg = tone === "sky" ? "bg-sky-50 border-sky-200" : "bg-emerald-50 border-emerald-200";
  const txt = tone === "sky" ? "text-sky-900" : "text-emerald-900";
  return (
    <div className={`rounded border ${bg} p-4`}>
      <div className={`text-xs font-semibold uppercase tracking-wide ${txt} opacity-70`}>{label}</div>
      <div className={`mt-1 text-xl font-bold ${txt}`}>{value}</div>
      {hint && <div className={`mt-1 text-xs ${txt} opacity-70`}>{hint}</div>}
    </div>
  );
}

function BreakdownRow({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return (
    <div className="px-4 py-2.5 flex items-start justify-between">
      <div className="pr-4">
        <div className="text-sm text-[#3f4254]">{label}</div>
        {sub && <div className="text-xs text-[#7e8299]">{sub}</div>}
      </div>
      <div className="text-sm font-semibold font-mono text-[#181c32] shrink-0">{value}</div>
    </div>
  );
}
